"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowUpRight, Lock, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { isFavoriteTool, toggleFavoriteTool } from "@/lib/favorite-tools";
import { lockedToolHref } from "@/lib/billing/planLabel";
import type { HubTool } from "./hubData";
import { HubBadge } from "./HubBadge";

export function HubToolCard({
  tool,
  locked = false,
  imageUrl = null,
}: {
  tool: HubTool;
  locked?: boolean;
  imageUrl?: string | null;
}) {
  const Icon = tool.icon;
  const href = locked ? lockedToolHref(tool.featureKey) : tool.href;

  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    setFavorited(isFavoriteTool(tool.href));
    const onChange = () => setFavorited(isFavoriteTool(tool.href));
    window.addEventListener("workshop-favorites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("workshop-favorites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [tool.href]);

  return (
    <div className="group relative h-full">
      <Link
        href={href}
        aria-disabled={locked}
        className={cn(
          "flex h-full min-h-[92px] items-start gap-3 rounded-2xl border border-border/70 bg-card/70 p-3.5 pr-9 transition duration-200",
          "hover:-translate-y-0.5 hover:border-accent/40 hover:bg-accent/5",
        )}
      >
        {imageUrl ? (
          <span className="relative grid h-12 w-16 flex-none place-items-center overflow-hidden rounded-xl border border-border/70 bg-secondary/60">
            <img
              src={imageUrl}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover"
            />
          </span>
        ) : (
          <span className="grid h-10 w-10 flex-none place-items-center rounded-xl border border-border/70 bg-secondary/60 text-foreground/85 transition group-hover:border-accent/35 group-hover:text-accent">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold tracking-tight text-foreground">
              {tool.title}
            </span>
            {locked ? <LockedChip /> : tool.badge ? <HubBadge kind={tool.badge} /> : null}
          </span>
          <span className="mt-0.5 line-clamp-2 block min-h-[2.05rem] text-[11.5px] leading-snug text-muted-foreground">
            {tool.blurb}
          </span>
        </span>
        {locked ? null : (
          <ArrowUpRight className="h-3.5 w-3.5 flex-none text-muted-foreground/0 transition group-hover:text-accent" />
        )}
      </Link>
      {locked ? null : (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const next = toggleFavoriteTool(tool.href);
            setFavorited(next);
          }}
          aria-label={favorited ? `Remove ${tool.title} from favorites` : `Favorite ${tool.title}`}
          title={favorited ? "Unfavorite" : "Favorite"}
          className={cn(
            "absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-md transition",
            favorited
              ? "text-amber-400 hover:bg-amber-400/10"
              : "text-muted-foreground/40 opacity-0 hover:bg-muted/40 hover:text-foreground group-hover:opacity-100 focus:opacity-100",
          )}
        >
          <Star className={cn("h-3.5 w-3.5", favorited ? "fill-current" : "")} />
        </button>
      )}
    </div>
  );
}

function LockedChip() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/40 px-1.5 py-px font-mono text-[8.5px] uppercase tracking-[0.18em] text-muted-foreground">
      <Lock className="h-2.5 w-2.5" />
      Locked
    </span>
  );
}
